/**
 * Role helpers for post-login routing and institution-only experience.
 * JWT / localStorage roles use names like ROLE_INSTITUTION_ADMIN, ROLE_ADMIN.
 */
import { getTenantBrandingParams } from './tenantHost';
import { consumeExamPrepDestination } from '../constants/examPrep';
import { needsPreparationChoice, finishPreparationChoice } from './preparationJourney';

export function isPlatformStaff(roles) {
  const R = roles || [];
  return R.some((r) =>
    ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_MODERATOR', 'ADMIN', 'SUPER_ADMIN', 'MODERATOR'].includes(r)
  );
}

export function isInstitutionAdmin(roles) {
  const R = roles || [];
  return R.some((r) => r === 'ROLE_INSTITUTION_ADMIN' || r === 'INSTITUTION_ADMIN');
}

/** Institution admin without platform staff roles — dedicated /institutional experience. */
export function isInstitutionOnlyAdmin(roles) {
  return isInstitutionAdmin(roles) && !isPlatformStaff(roles);
}

/**
 * Learner who registered on a school-branded host (JWT includes homeInstitutionId).
 * Gets a minimal portal — not the public course catalog / marketing shell.
 */
export function isInstitutionHomeLearner(user) {
  if (!user || user.homeInstitutionId == null) {
    return false;
  }
  const roles = user.roles || [];
  if (isPlatformStaff(roles) || isInstitutionAdmin(roles)) {
    return false;
  }
  return true;
}

/**
 * Paths learners with a home institution may open without redirect to /institutional/student.
 * @param {string} pathname
 * @param {object} [features] - tenant features from TenantEntitlementsContext (plan-gated)
 */
export function isPathAllowedForInstitutionHomeLearner(pathname, features) {
  const always = [
    '/institutional/student',
    '/typing-display',
    '/start-test',
    '/profile',
    '/user-result',
    '/test-details',
  ];
  if (always.some(p => pathname.startsWith(p))) return true;

  const f = features || {};
  if (f.typingLessons && pathname.startsWith('/learn')) return true;
  if (f.steno && pathname.startsWith('/steno')) {
    if (pathname.startsWith('/steno/learn') && !f.stenoLessons) {
      return false;
    }
    return true;
  }
  if (f.playHub && (
    pathname.startsWith('/games') ||
    pathname.startsWith('/live-exams') ||
    pathname.startsWith('/tournaments') ||
    pathname.startsWith('/exam')
  )) return true;
  if (f.blog && pathname.startsWith('/blog')) return true;
  if (f.community && pathname.startsWith('/community')) return true;
  if (f.referral && pathname.startsWith('/referral')) return true;
  if (f.socialFriends && pathname.startsWith('/friends')) return true;

  const exact = [
    '/login', '/register', '/forgot-password', '/reset-password',
    '/verify-otp', '/privacy-policy', '/terms-of-service',
    '/cookie-policy', '/refund-policy', '/faq', '/help',
  ];
  return exact.includes(pathname);
}

/**
 * Default path after successful login / Google sign-in.
 * @param {object|Array} sessionOrRoles — full session object from API/localStorage, or legacy roles array only
 */
export function getPostLoginPath(sessionOrRoles) {
  const examPrepDestination = consumeExamPrepDestination();
  const isRawRoles = Array.isArray(sessionOrRoles);
  const session = isRawRoles ? { roles: sessionOrRoles } : sessionOrRoles || {};
  const roles = session.roles || [];
  if (isInstitutionOnlyAdmin(roles)) {
    finishPreparationChoice();
    return '/institutional/dashboard';
  }
  if (!isRawRoles && isInstitutionHomeLearner(session)) {
    finishPreparationChoice();
    return '/institutional/student';
  }
  const tenantParams = getTenantBrandingParams();
  if (tenantParams) {
    finishPreparationChoice();
    return '/institutional/student';
  }
  if (examPrepDestination || isPlatformStaff(roles)) finishPreparationChoice();
  return examPrepDestination || (!isPlatformStaff(roles) && needsPreparationChoice() ? '/get-started' : '/');
}

/** Paths institution-only admins may open without being redirected back to the dashboard. */
export function isPathAllowedForInstitutionOnlyUser(pathname) {
  if (
    pathname.startsWith('/institutional') ||
    pathname.startsWith('/profile')
  ) {
    return true;
  }
  const exact = [
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/verify-otp',
  ];
  return exact.includes(pathname);
}
